import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import classes from './AgentButtonGroup.module.scss';

import { AgentStatus } from '../../../types/enums';
import { TicketStatus } from '../../../types/enums';
import { useTicketStatus } from '../../../hooks/Agents/useTicketStatus';
import { AgentsTicketType } from '../../../pagesMain/Agent1Page/Agent1Page';
import {
  OperatorPresence,
  useOperatorPresence,
} from '../../../hooks/Agents/useOperatorPresence';

type AgentButtonGroupPropsType = {
  agentStatus: AgentStatus;
  ticketStatus: string;
  handleChangeAgentStatus: (status: AgentStatus) => void;
  setActiveTicket: (ticket: AgentsTicketType | null) => void;
  setEntryTicket?: (entryTicket: number | null) => void;
  ticketId: number;
  activeItems: AgentsTicketType[] | [];
  setModalRedirectID: (active: number) => void;
  setActiveModalInWorking: (value: boolean) => void;
  queueStatus: boolean | null;
  isActiveModalInWorking: boolean;
  isDisabledButtonLoading?: boolean;
  isWorkPlaceSelected: boolean;
  timeToRequest: number | null;
};

const MANUAL_PRESENCE: { value: OperatorPresence; label: string }[] = [
  { value: 'FREE', label: 'Свободен' },
  { value: 'BUSY', label: 'Занят' },
  { value: 'BREAK', label: 'Перерыв' },
];

const AgentButtonGroup: FC<AgentButtonGroupPropsType> = ({
  agentStatus,
  handleChangeAgentStatus,
  ticketId,
  setActiveTicket,
  activeItems,
  setModalRedirectID,
  setActiveModalInWorking,
  setEntryTicket,
  queueStatus,
  isActiveModalInWorking,
  isDisabledButtonLoading,
  isWorkPlaceSelected,
  timeToRequest,
}) => {
  const { mutate: changeTicketStatus } = useTicketStatus(agentStatus, setActiveModalInWorking);
  const statusRequestInFlightRef = useRef(false);
  const { presenceData, setPresence, isUpdatingPresence } = useOperatorPresence(
    isWorkPlaceSelected,
    { polling: false }
  );
  const [now, setNow] = useState(Date.now());
  const [autoCallEndsAtMs, setAutoCallEndsAtMs] = useState<number | null>(null);
  const noShowHandOffDoneRef = useRef(false);
  const autoNoShowFiredRef = useRef(false);

  const noShowTimeoutSec = presenceData?.noShowTimeoutSeconds ?? 0;
  const autoDistributeEnabled = Boolean(presenceData?.autoDistributeEnabled);
  const autoDistributeDelaySec = presenceData?.autoDistributeDelaySeconds ?? 0;
  const autoCallRemainingFromServer = presenceData?.autoCallRemainingSeconds ?? null;
  const manualBlocked =
    presenceData?.presence === 'BUSY' ||
    presenceData?.presence === 'BREAK' ||
    presenceData?.presence === 'ABSENT';

  const noShowLeftSec = useMemo(() => {
    if (
      agentStatus !== AgentStatus.Requesting ||
      !autoDistributeEnabled ||
      !noShowTimeoutSec ||
      !timeToRequest
    ) {
      return null;
    }
    const elapsedSec = Math.floor((now - timeToRequest) / 1000);
    return Math.max(0, noShowTimeoutSec - elapsedSec);
  }, [agentStatus, autoDistributeEnabled, noShowTimeoutSec, timeToRequest, now]);

  // Новый вызов клиента — сбрасываем флаги авто-неявки.
  useEffect(() => {
    if (agentStatus === AgentStatus.Requesting && noShowLeftSec !== null && noShowLeftSec > 0) {
      noShowHandOffDoneRef.current = false;
      autoNoShowFiredRef.current = false;
    }
    if (agentStatus === AgentStatus.Working || manualBlocked) {
      noShowHandOffDoneRef.current = false;
      autoNoShowFiredRef.current = false;
      setAutoCallEndsAtMs(null);
    }
  }, [agentStatus, noShowLeftSec, manualBlocked]);

  // При 0 на «не пришел» — автоматически закрываем талон (как нажатие кнопки) и стартуем отсчёт автовызова.
  useEffect(() => {
    if (
      !autoDistributeEnabled ||
      agentStatus !== AgentStatus.Requesting ||
      noShowLeftSec === null ||
      noShowLeftSec > 0 ||
      !ticketId ||
      isDisabledButtonLoading ||
      autoNoShowFiredRef.current
    ) {
      return;
    }
    autoNoShowFiredRef.current = true;

    handleChangeAgentStatus(AgentStatus.Waiting);
    changeTicketStatus({
      ticketId,
      requestStatus: TicketStatus.DeleteClientInQueue,
    });
    setEntryTicket?.(null);
    setActiveTicket(null);

    if (autoDistributeEnabled && autoDistributeDelaySec > 0 && !manualBlocked) {
      noShowHandOffDoneRef.current = true;
      setAutoCallEndsAtMs(Date.now() + autoDistributeDelaySec * 1000);
    }
  }, [
    agentStatus,
    noShowLeftSec,
    ticketId,
    isDisabledButtonLoading,
    autoDistributeEnabled,
    autoDistributeDelaySec,
    manualBlocked,
    handleChangeAgentStatus,
    changeTicketStatus,
    setEntryTicket,
    setActiveTicket,
  ]);

  const canShowAutoCallCountdown =
    autoDistributeEnabled &&
    autoDistributeDelaySec > 0 &&
    !manualBlocked &&
    autoCallEndsAtMs != null &&
    agentStatus === AgentStatus.Waiting;

  // Если оператор уже в Waiting (не после неявки) — якорим отсчёт по серверу.
  useEffect(() => {
    if (
      agentStatus !== AgentStatus.Waiting ||
      !autoDistributeEnabled ||
      autoDistributeDelaySec <= 0 ||
      manualBlocked
    ) {
      return;
    }
    // Локальный отсчёт после неявки уже идёт — не перетираем серверным «остатком».
    if (noShowHandOffDoneRef.current) {
      return;
    }

    let remainingSec = autoCallRemainingFromServer;
    if (remainingSec == null || remainingSec <= 0) {
      const changedAtMs = presenceData?.presenceChangedAt
        ? Date.parse(presenceData.presenceChangedAt)
        : NaN;
      if (!Number.isNaN(changedAtMs)) {
        remainingSec = autoDistributeDelaySec - Math.floor((Date.now() - changedAtMs) / 1000);
      } else {
        remainingSec = autoDistributeDelaySec;
      }
    }

    if (remainingSec == null || remainingSec <= 0) {
      setAutoCallEndsAtMs(null);
      return;
    }

    setAutoCallEndsAtMs(Date.now() + remainingSec * 1000);
  }, [
    agentStatus,
    autoDistributeEnabled,
    autoDistributeDelaySec,
    autoCallRemainingFromServer,
    presenceData?.presenceChangedAt,
    manualBlocked,
  ]);

  const needCountdownTick =
    (autoDistributeEnabled && agentStatus === AgentStatus.Requesting && noShowTimeoutSec > 0) ||
    autoCallEndsAtMs != null;

  useEffect(() => {
    if (!needCountdownTick) {
      return;
    }
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [needCountdownTick, autoCallEndsAtMs]);

  const countdownLabel = useMemo(() => {
    if (noShowLeftSec === null || noShowLeftSec <= 0) {
      return null;
    }
    const mm = String(Math.floor(noShowLeftSec / 60)).padStart(2, '0');
    const ss = String(noShowLeftSec % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [noShowLeftSec]);

  const autoCallCountdownLabel = useMemo(() => {
    if (!canShowAutoCallCountdown || autoCallEndsAtMs == null) {
      return null;
    }
    const left = Math.max(0, Math.ceil((autoCallEndsAtMs - now) / 1000));
    if (left <= 0) {
      return null;
    }
    const mm = String(Math.floor(left / 60)).padStart(2, '0');
    const ss = String(left % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [canShowAutoCallCountdown, autoCallEndsAtMs, now]);

  const handleClick = (nextStatus: AgentStatus, requestStatus: TicketStatus, id: number) => {
    if (statusRequestInFlightRef.current) {
      return;
    }
    statusRequestInFlightRef.current = true;
    handleChangeAgentStatus(nextStatus);
    changeTicketStatus(
      { ticketId: id, requestStatus },
      {
        onSettled: () => {
          statusRequestInFlightRef.current = false;
        },
      }
    );
  };
  const handleClickNextTicket = (
    nextStatus: AgentStatus,
    requestStatus: TicketStatus,
    id: number
  ) => {
    setActiveTicket(activeItems[0]);
    handleClick(nextStatus, requestStatus, id);
  };

  const handleClickFinishTicket = (
    nextStatus: AgentStatus,
    requestStatus: TicketStatus,
    id: number
  ) => {
    handleClick(nextStatus, requestStatus, id);
    setEntryTicket && setEntryTicket(null);
    setActiveTicket(null);
  };

  useEffect(() => {
    if (isActiveModalInWorking) {
      handleChangeAgentStatus(AgentStatus.Waiting);
      setActiveTicket(null);
    }
  }, [isActiveModalInWorking]);

  useEffect(() => {
    if (!presenceData) return;
    if (presenceData.presence === 'WAITING_PATIENT' && agentStatus === AgentStatus.Waiting) {
      // автораспределение: статус на сервере уже «ожидает пациента»
    }
  }, [presenceData, agentStatus]);

  return (
    <div className={classes.wrapper}>
      <div className={classes.presenceBar}>
        <div className={classes.presenceButtons}>
          {MANUAL_PRESENCE.map((item) => (
            <button
              key={item.value}
              type="button"
              className={
                presenceData?.presence === item.value
                  ? classes.presenceActive
                  : classes.presenceBtn
              }
              disabled={isUpdatingPresence}
              onClick={() => setPresence(item.value)}>
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <div
        className={`${classes.container}${
          autoDistributeEnabled ? ` ${classes.containerTimed}` : ''
        }`}>
        <button
          type="button"
          onClick={() =>
            handleClickNextTicket(
              AgentStatus.Requesting,
              TicketStatus.CallFirstClientInQueue,
              activeItems[0].id
            )
          }
          className={classes.nextBtn}
          disabled={
            agentStatus !== AgentStatus.Waiting ||
            !queueStatus ||
            isDisabledButtonLoading ||
            manualBlocked
          }>
          <span className={classes.btnLabel}>следующий</span>
          <span
            className={`${classes.btnCountdown}${
              autoCallCountdownLabel ? '' : ` ${classes.btnCountdownHidden}`
            }`}>
            {autoCallCountdownLabel ?? '00:00'}
          </span>
        </button>
        <button
          type="button"
          disabled={
            agentStatus !== AgentStatus.Waiting ||
            !ticketId ||
            isDisabledButtonLoading ||
            manualBlocked
          }
          onClick={() =>
            handleClick(AgentStatus.Requesting, TicketStatus.CallPriorityClientinQueue, ticketId)
          }>
          <span className={classes.btnLabel}>вызов</span>
        </button>
        <button
          type="button"
          disabled={agentStatus !== AgentStatus.Requesting || isDisabledButtonLoading}
          onClick={() => handleClick(AgentStatus.Working, TicketStatus.TakeInWorking, ticketId)}>
          <span className={classes.btnLabel}>в работу</span>
        </button>
        <button
          type="button"
          disabled={agentStatus !== AgentStatus.Working || isDisabledButtonLoading}
          className={classes.redirectBtn}
          onClick={() => {
            setModalRedirectID(ticketId);
          }}>
          <span className={classes.btnLabel}>перенаправить</span>
        </button>
        <button
          type="button"
          disabled={agentStatus !== AgentStatus.Requesting || isDisabledButtonLoading}
          onClick={() =>
            handleClick(AgentStatus.Requesting, TicketStatus.RepaetedCallClient, ticketId)
          }>
          <span className={classes.btnLabel}>повторный вызов</span>
        </button>
        <button
          type="button"
          disabled={agentStatus !== AgentStatus.Requesting || isDisabledButtonLoading}
          onClick={() =>
            handleClickFinishTicket(AgentStatus.Waiting, TicketStatus.DeleteClientInQueue, ticketId)
          }>
          <span className={classes.btnLabel}>не пришел</span>
          <span
            className={`${classes.btnCountdown}${
              countdownLabel ? '' : ` ${classes.btnCountdownHidden}`
            }`}>
            {countdownLabel ?? '00:00'}
          </span>
        </button>
        <button
          type="button"
          disabled={agentStatus !== AgentStatus.Working || isDisabledButtonLoading}
          onClick={() =>
            handleClickFinishTicket(AgentStatus.Waiting, TicketStatus.FinishClientInQueue, ticketId)
          }>
          <span className={classes.btnLabel}>завершить работу</span>
        </button>
      </div>
    </div>
  );
};

export default AgentButtonGroup;
